/**
 * 画面が固まったあとに出る「ログをコピー」のカード（PRODUCT_DEV-1115・DEV 専用）。
 *
 * 固まっている最中は何も押せないので、動き出した直後にこのカードを画面下に出す。
 * コピーしたテキストを Slack に貼ってもらえば、どの処理が何秒止めたかを調べられる。
 * 固まりを検知していなくても、画面上端の色帯を長押しすると開ける（`EnvironmentBanner`）。
 *
 * 記録の本体は `lib/freezeRecorder.ts`。記録が動いていない（DEV 以外）ときは何も描かない。
 */

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Copy, X } from "lucide-react";
import {
  createFreezeReport,
  getFreezeState,
  isFreezeRecorderInstalled,
  subscribeFreeze,
} from "../../lib/freezeRecorder";
import { writeClipboardFromPromise } from "../../lib/meetings/writeClipboard";
import { Toast } from "./Toast";

export function FreezeReportCard() {
  const state = useSyncExternalStore(subscribeFreeze, getFreezeState, getFreezeState);
  // 閉じたときの印。新しい固まり・長押しで印が変わったら、また開く
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const [fallbackText, setFallbackText] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 新しい固まりを検知したら・色帯の長押しで頼まれたら開く
  const openKey =
    state.latest || state.openRequests > 0
      ? `${state.latest?.id ?? 0}:${state.latest?.durationMs ?? 0}:${state.openRequests}`
      : null;
  const open = openKey !== null && openKey !== dismissedKey;

  useEffect(() => {
    if (fallbackText !== null) textareaRef.current?.select();
  }, [fallbackText]);

  if (!isFreezeRecorderInstalled()) return null;

  const handleCopy = () => {
    // 本文の組み立てを待つ前に書き込みを始める（タップの直後でないとブラウザが拒む）
    const text = createFreezeReport();
    writeClipboardFromPromise(text)
      .then(() => {
        setDismissedKey(openKey);
        setToast("コピーしました。Slack に貼ってください");
      })
      .catch(async () => {
        // コピーできない端末では全文を出して、手で選んでコピーしてもらう
        setFallbackText(await text);
      });
  };

  const close = () => {
    setDismissedKey(openKey);
    setFallbackText(null);
  };

  const latest = state.latest;
  const title = latest
    ? `画面が ${(latest.durationMs / 1000).toFixed(1)} 秒固まりました`
    : "固まりログ";

  return (
    <>
      {open && (
        <div
          role="dialog"
          aria-label="固まりログ"
          className="fixed inset-x-4 z-[9998] mx-auto max-w-md rounded-xl border border-slate-200 bg-white p-4 shadow-lg"
          style={{ bottom: "calc(env(safe-area-inset-bottom) + 88px)" }}
        >
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <p className="text-sm font-bold text-[#424242]">{title}</p>
              <p className="mt-1 text-xs text-[#757575]">
                DEV だけの表示です。ログをコピーして Slack に貼ってください（PRODUCT_DEV-1115）
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="閉じる"
              className="rounded-lg p-1 text-[#757575] active:bg-slate-100"
            >
              <X className="h-5 w-5" strokeWidth={2} />
            </button>
          </div>
          {fallbackText === null ? (
            <button
              type="button"
              onClick={handleCopy}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-bold text-white active:opacity-80"
            >
              <Copy className="h-4 w-4" strokeWidth={2} />
              ログをコピー
            </button>
          ) : (
            <>
              <p className="mt-3 text-xs text-[#757575]">
                自動でコピーできませんでした。下の欄を長押しして全部選び、コピーしてください
              </p>
              <textarea
                ref={textareaRef}
                readOnly
                value={fallbackText}
                className="mt-2 h-40 w-full rounded-lg border border-slate-200 p-2 font-mono text-xs text-[#424242]"
              />
            </>
          )}
        </div>
      )}
      {toast && (
        <Toast message={toast} type="success" variant="dark-bottom" onClose={() => setToast(null)} />
      )}
    </>
  );
}
